import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Anchor,
  Box,
  Breadcrumbs,
  Button,
  Card,
  Container,
  Group,
  LoadingOverlay,
  Modal,
  TextInput,
  Title,
  ScrollArea,
  Grid,
  NumberInput,
  Text,
  Select,
  ActionIcon,
  SegmentedControl,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { Link } from "react-router-dom";
import { ADMIN_PATH, ADMIN_PRONOSTICSV2_PATH } from "_urls";
import { z } from "zod";
import {
  useGetPlayerQuery,
  useUpdatePlayerMutation,
} from "quiz/api/players.endpoints";
import Collapsable from "common/admin/ui/Collapsable";
import { PronosticV2 } from "pronosticsv2/types/pronosticv2.model";
import { CirclePlus, Trash } from "tabler-icons-react";

type Prediction = {
  index: number;
  values: Array<string | number>;
};

const playerSchema = z.object({
  id: z.string(),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email"),
  phoneNumber: z.string().min(1, "Phone number is required"),
  termsOfService: z.boolean(),
  pronosticInfo: z.object({
    subPlace: z.string().optional(),
    prediction: z
      .array(
        z.object({
          index: z.number(),
          values: z.array(z.union([z.number(), z.string()])),
        })
      )
      .optional(),
  }),
});

const getSubplaceTitle = (partitionId: string, pronostic: PronosticV2) =>
  pronostic?.commonSettings?.general?.moduleSettings?.place?.subPlaces?.find(
    (s) => s.id === partitionId?.split("$")[1]
  )?.title || "";

export function PlayerEditModal({
  playerId,
  partitionId,
  opened,
  setOpened,
  pronostic,
  predictionlength,
  onSave,
}: {
  playerId: string;
  partitionId: string;
  opened: boolean;
  setOpened: (opened: boolean) => void;
  pronostic: PronosticV2;
  predictionlength: number;
  onSave?: () => void;
}) {
  const [updatePlayer] = useUpdatePlayerMutation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newPredictionFields, setNewPredictionFields] = useState<1 | 2>(1);
  const [fieldType, setFieldType] = useState<"text" | "number">("number");

  const { data: player } = useGetPlayerQuery([playerId, partitionId]);

  void error;

  const form = useForm({
    initialValues: {
      id: playerId,
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      room: "",
      enterprise: "",
      termsOfService: false,
      pronosticInfo: {
        subPlace: "",
        prediction: [] as Prediction[],
      },
    },
    validate: (values) => {
      const result = playerSchema.safeParse(values);
      return result.success ? {} : result.error.formErrors.fieldErrors;
    },
  });

  useEffect(() => {
    if (player) {
      form.setValues({
        ...player,
        pronosticInfo: {
          subPlace: player.pronosticInfo?.subPlace || "",
          prediction: player.pronosticInfo?.prediction || [],
        },
      });
    }
    // eslint-disable-next-line
  }, [player]);

  const isDirty = useMemo(
    () => JSON.stringify(form.values) !== JSON.stringify(player),
    [form.values, player]
  );

  const handleAddPrediction = useCallback(() => {
    const newPrediction: Prediction = {
      index: form.values.pronosticInfo.prediction.length + 1,
      values:
        newPredictionFields === 1 ? [fieldType === "text" ? "" : 0] : [0, 0],
    };
    form.setFieldValue("pronosticInfo.prediction", [
      ...form.values.pronosticInfo.prediction,
      newPrediction,
    ]);
  }, [form, newPredictionFields, fieldType]);

  const handleDeletePrediction = useCallback(
    (index: number) => {
      const updatedPredictions = form.values.pronosticInfo.prediction
        .filter((_, i) => i !== index)
        .map((pred, newIndex) => ({
          ...pred,
          index: newIndex + 1, // Reindex starting from 1
        }));
      form.setFieldValue("pronosticInfo.prediction", updatedPredictions);
    },
    [form]
  );

  const handleSave = useCallback(async () => {
    const validationResult = playerSchema.safeParse(form.values);
    if (!validationResult.success) {
      Object.entries(validationResult.error.formErrors.fieldErrors).forEach(
        ([field, messages]) => form.setFieldError(field, messages?.join(", "))
      );
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        ...player,
        ...form.values,
      };
      await updatePlayer(payload).unwrap();
      setOpened(false);
      onSave?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update player");
    } finally {
      setIsLoading(false);
    }
  }, [form, updatePlayer, setOpened, onSave, player]);

  return (
    <Modal
      opened={opened}
      onClose={() => setOpened(false)}
      fullScreen
      radius={0}
      withCloseButton={false}
    >
      {isLoading && <LoadingOverlay visible overlayOpacity={0.8} />}
      <Box
        p="lg"
        sx={(theme) => ({
          background:
            theme.colorScheme === "dark"
              ? theme.colors.gray[8]
              : theme.colors.gray[2],
          minHeight: "calc(100vh - 64px)",
        })}
      >
        <Container className="min-h-full">
          {/* Header */}
          <div
            className="sticky top-0 z-50 pb-4"
            style={{ backgroundColor: "#E9ECEF" }}
          >
            <Title order={3} ta="center" my="xs">
              {player?.firstName} {player?.lastName}
            </Title>
            <Breadcrumbs separator="→">
              <Anchor component={Link} to={`/${ADMIN_PATH}`}>
                Admin
              </Anchor>
              <Anchor
                component={Link}
                to={`/${ADMIN_PATH}/${ADMIN_PRONOSTICSV2_PATH}`}
              >
                Pronostics
              </Anchor>
              <Anchor onClick={() => setOpened(false)}>SubPlace Form</Anchor>
            </Breadcrumbs>
            <Group position="right">
              <Button
                disabled={!isDirty}
                onClick={handleSave}
                className="bg-button-admin disabled:opacity-75"
              >
                Save
              </Button>
            </Group>
          </div>

          {/* Form Content */}
          <ScrollArea h="calc(100vh - 180px)" type="scroll">
            <Card shadow="sm" p="xl" mt="md">
              <Grid columns={2}>
                <Grid.Col span={1}>
                  <TextInput
                    label="First Name"
                    required
                    {...form.getInputProps("firstName")}
                  />
                </Grid.Col>
                <Grid.Col span={1}>
                  <TextInput
                    label="Last Name"
                    required
                    {...form.getInputProps("lastName")}
                  />
                </Grid.Col>
                <Grid.Col span={1}>
                  <TextInput
                    label="Email"
                    required
                    {...form.getInputProps("email")}
                  />
                </Grid.Col>
                <Grid.Col span={1}>
                  <TextInput
                    label="Phone"
                    required
                    {...form.getInputProps("phoneNumber")}
                  />
                </Grid.Col>

                <Grid.Col span={1}>
                  <TextInput
                    label="Salon"
                    required
                    {...form.getInputProps("room")}
                  />
                </Grid.Col>

                <Grid.Col span={1}>
                  <TextInput
                    label="Societé"
                    required
                    {...form.getInputProps("enterprise")}
                  />
                </Grid.Col>

                <Grid.Col span={1}>
                  <TextInput
                    label="Subplace"
                    required
                    disabled
                    value={getSubplaceTitle(partitionId, pronostic)}
                  />
                </Grid.Col>
              </Grid>
            </Card>

            {/* Predictions Section */}
            <div className="mt-10">
              <Collapsable title="Pronostics">
                <Box mt="md">
                  {form.values.pronosticInfo.prediction.length === 0 ? (
                    <Text c="dimmed" ta="center" py="lg">
                      No predictions submitted
                    </Text>
                  ) : (
                    form.values.pronosticInfo.prediction.map(
                      (pred, predIndex) => (
                        <Box key={predIndex} mb="md">
                          <Group>
                            <Text fw={500} mb="xs">
                              Prono#{pred.index}
                            </Text>
                            <ActionIcon
                              onClick={() => handleDeletePrediction(predIndex)}
                              className="ml-40"
                            >
                              <Trash color="red" size={40} />
                            </ActionIcon>
                          </Group>
                          <Group className="grid grid-cols-2 w-[25vw]">
                            {pred.values.map((value, valueIndex) =>
                              typeof value === "string" ? (
                                <TextInput
                                  key={valueIndex}
                                  {...form.getInputProps(
                                    `pronosticInfo.prediction.${predIndex}.values.${valueIndex}`
                                  )}
                                  mb="xs"
                                />
                              ) : (
                                <NumberInput
                                  key={valueIndex}
                                  hideControls
                                  {...form.getInputProps(
                                    `pronosticInfo.prediction.${predIndex}.values.${valueIndex}`
                                  )}
                                  mb="xs"
                                />
                              )
                            )}
                          </Group>
                        </Box>
                      )
                    )
                  )}

                  {/* Field Selection and Add Prediction */}
                  <Group align="flex-end" mt="md">
                    <Select
                      label="Number of Fields"
                      value={newPredictionFields.toString()}
                      onChange={(value) => {
                        const numFields = Number(value) as 1 | 2;
                        setNewPredictionFields(numFields);
                        // Reset to number when switching to 2 fields
                        if (numFields === 2) {
                          setFieldType("number");
                        }
                      }}
                      data={[
                        { value: "1", label: "1 Field" },
                        { value: "2", label: "2 Fields" },
                      ]}
                      style={{ width: 120 }}
                    />

                    {/* Field Type Selection - Only for 1 field */}
                    {newPredictionFields === 1 && (
                      <Box>
                        <Text size="xs" mb={2}>
                          Field Type
                        </Text>
                        <SegmentedControl
                          value={fieldType}
                          onChange={(value) =>
                            setFieldType(value as "text" | "number")
                          }
                          data={[
                            { label: "Text", value: "text" },
                            { label: "Number", value: "number" },
                          ]}
                          size="xs"
                          style={{ width: 140 }}
                        />
                      </Box>
                    )}

                    <Button
                      variant="subtle"
                      leftIcon={<CirclePlus />}
                      onClick={handleAddPrediction}
                      disabled={
                        form.values.pronosticInfo.prediction.length >=
                        predictionlength
                      }
                    >
                      Add Prediction
                    </Button>
                  </Group>
                </Box>
              </Collapsable>
            </div>
          </ScrollArea>
        </Container>
      </Box>
    </Modal>
  );
}
